import React, { useMemo, useRef, useState, useEffect } from 'react';


import { supabase } from "../supabaseClient";
import { routes } from "../routes";
import bcrypt from "bcryptjs";

const API_BASE = "https://ubicaciones.paginasweb.cr";

export default function RegistroPage({ onNavigate }) {
  const [formData, setFormData] = useState({
    nombre: "",
    correo: "",
    celular: "",
    contrasena: "",

    requiereFE: false,
    tipoCedula: "01",           // 01 Física (default)
    numeroCedula: "",
    razonSocial: "",
    correoFacturacion: "",

    provinciaCode: "",
    provinciaName: "",
    cantonCode: "",
    cantonName: "",
    distritoCode: "",
    distritoName: "",
    otrasSenas: "",
  });

  const [loading, setLoading] = useState(false);

  // Catálogos
  const [provincias, setProvincias] = useState([]);    // [{code, name}]
  const [cantones, setCantones] = useState([]);        // [{code, name}]
  const [distritos, setDistritos] = useState([]);      // [{code, name}]

  // Cargar provincias al montar
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/provincias.json`);
        const json = await resp.json(); // { "1": "San José", ... }
        const list = Object.entries(json).map(([code, name]) => ({ code, name }));
        setProvincias(list);
      } catch (e) {
        console.error("Error cargando provincias:", e);
      }
    })();
  }, []);

  // Al cambiar provincia -> cargar cantones
  useEffect(() => {
    if (!formData.provinciaCode) {
      setCantones([]);
      setDistritos([]);
      return;
    }
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/provincia/${formData.provinciaCode}/cantones.json`);
        const json = await resp.json(); // { "01": "Central", "02": "Escazú", ... }
        const list = Object.entries(json).map(([code, name]) => ({ code, name }));
        setCantones(list);
        // reset dependientes
        setFormData((prev) => ({
          ...prev,
          cantonCode: "",
          cantonName: "",
          distritoCode: "",
          distritoName: "",
        }));
        setDistritos([]);
      } catch (e) {
        console.error("Error cargando cantones:", e);
      }
    })();
  }, [formData.provinciaCode]);

  // Al cambiar cantón -> cargar distritos
  useEffect(() => {
    if (!formData.provinciaCode || !formData.cantonCode) {
      setDistritos([]);
      return;
    }
    (async () => {
      try {
        const resp = await fetch(
          `${API_BASE}/provincia/${formData.provinciaCode}/canton/${formData.cantonCode}/distritos.json`
        );
        const json = await resp.json(); // { "01": "Carmen", "02": "Merced", ... }
        const list = Object.entries(json).map(([code, name]) => ({ code, name }));
        setDistritos(list);
        // reset distrito
        setFormData((prev) => ({
          ...prev,
          distritoCode: "",
          distritoName: "",
        }));
      } catch (e) {
        console.error("Error cargando distritos:", e);
      }
    })();
  }, [formData.provinciaCode, formData.cantonCode]);

  // Helpers de cambio
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // selects de ubicación: guardar code + name
    if (name === "provinciaCode") {
      const sel = provincias.find((p) => p.code === value);
      setFormData((prev) => ({
        ...prev,
        provinciaCode: value,
        provinciaName: sel?.name || "",
      }));
      return;
    }
    if (name === "cantonCode") {
      const sel = cantones.find((c) => c.code === value);
      setFormData((prev) => ({
        ...prev,
        cantonCode: value,
        cantonName: sel?.name || "",
      }));
      return;
    }
    if (name === "distritoCode") {
      const sel = distritos.find((d) => d.code === value);
      setFormData((prev) => ({
        ...prev,
        distritoCode: value,
        distritoName: sel?.name || "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validaciones mínimas (si requiere FE)
      if (formData.requiereFE) {
        if (!formData.tipoCedula || !formData.numeroCedula) {
          alert("Por favor, completa el tipo y número de cédula para Factura Electrónica.");
          setLoading(false);
          return;
        }
      }

      const hashedPassword = await bcrypt.hash(formData.contrasena, 10);

      const { error } = await supabase.from("clientes").insert([{
  nombre: formData.nombre,
  correo: formData.correo,
  celular: formData.celular,
  contrasena_hash: hashedPassword,

  requiere_fe: formData.requiereFE,
  tipo_cedula: formData.requiereFE ? formData.tipoCedula : null,
  numero_cedula: formData.requiereFE ? formData.numeroCedula : null,
  razon_social: formData.requiereFE ? formData.razonSocial : null,
  correo_facturacion: formData.requiereFE ? formData.correoFacturacion : null,
  telefono_facturacion: formData.requiereFE ? (formData.telefonoFacturacion || null) : null,

  provincia: formData.provinciaName || null,
  canton: formData.cantonName || null,
  distrito: formData.distritoName || null,
  otras_senas: formData.otrasSenas || null,
}]);



      if (error) {
        console.error("Supabase insert error:", error);
        alert(`❌ No se pudo registrar: ${error.message || "Error desconocido"}`);
        setLoading(false);
        return;
      }

      alert("✅ Registro exitoso");
      if (onNavigate) onNavigate(routes.home);
    } catch (err) {
      console.error(err);
      alert("❌ Error inesperado al registrarse.");
    } finally {
      setLoading(false);
    }
  };

  // Opciones de tipo de cédula (código FE)
  const tiposCedula = useMemo(
    () => [
      { value: "01", label: "Física" },
      { value: "02", label: "Jurídica" },
      { value: "03", label: "DIMEX" },
      { value: "04", label: "NITE" },
    ],
    []
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-10">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
          Regístrate en <span className="text-[#5A61E5]">El Ancla CR</span>
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Datos básicos */}
          <input
            type="text"
            name="nombre"
            placeholder="Nombre completo"
            value={formData.nombre}
            onChange={handleChange}
            required
            className="w-full border rounded-lg p-3"
          />

          <input
            type="email"
            name="correo"
            placeholder="Correo electrónico"
            value={formData.correo}
            onChange={handleChange}
            required
            className="w-full border rounded-lg p-3"
          />

          <input
            type="number"
            name="celular"
            placeholder="Número de celular"
            value={formData.celular}
            onChange={handleChange}
            required
            className="w-full border rounded-lg p-3"
          />

          <input
            type="password"
            name="contrasena"
            placeholder="Contraseña"
            value={formData.contrasena}
            onChange={handleChange}
            required
            className="w-full border rounded-lg p-3"
          />

          {/* Factura Electrónica */}
          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              id="requiereFE"
              name="requiereFE"
              checked={formData.requiereFE}
              onChange={handleChange}
              className="mt-1"
            />
            <label htmlFor="requiereFE" className="text-sm text-gray-700">
              Necesito Factura Electrónica <br />
              <span className="text-xs text-gray-500">
                (Si estás registrado en Hacienda, por favor marca esta casilla)
              </span>
            </label>
          </div>

          {/* Campos FE opcionales */}
          {formData.requiereFE && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-1">
                <label className="block text-sm text-gray-600 mb-1">Tipo de cédula</label>
                <select
                  name="tipoCedula"
                  value={formData.tipoCedula}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-3 bg-white"
                >
                  {tiposCedula.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="block text-sm text-gray-600 mb-1">Número de cédula</label>
                <input
                  type="number"
                  name="numeroCedula"
                  placeholder="Ej: 201230123"
                  value={formData.numeroCedula}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-3"
                  required
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Razón Social</label>
                <input
                  type="text"
                  name="razonSocial"
                  placeholder=""
                  value={formData.razonSocial}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-3"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Correo de facturación</label>
                <input
                  type="email"
                  name="correoFacturacion"
                  placeholder="facturacion@correo.com"
                  value={formData.correoFacturacion}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-3"
                />
              </div>
                <div className="sm:col-span-2">
                    <input
  type="tel"
  name="telefonoFacturacion"
  placeholder="Teléfono de facturación"
  value={formData.telefonoFacturacion || ""}
  onChange={handleChange}
  className="w-full border rounded-lg p-3"
/>

                </div>
              
            </div>
          )}

          {/* Dirección */}
          <h3 className="text-lg font-semibold text-gray-800 mt-6">Dirección</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Provincia */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Provincia</label>
              <select
                name="provinciaCode"
                value={formData.provinciaCode}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 bg-white"
              >
                <option value="">Seleccione provincia</option>
                {provincias.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Cantón */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Cantón</label>
              <select
                name="cantonCode"
                value={formData.cantonCode}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 bg-white"
                disabled={!formData.provinciaCode}
              >
                <option value="">{formData.provinciaCode ? "Seleccione cantón" : "Seleccione provincia primero"}</option>
                {cantones.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Distrito */}
            <div className="sm:col-span-2">
              <label className="block text-sm text-gray-600 mb-1">Distrito</label>
              <select
                name="distritoCode"
                value={formData.distritoCode}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 bg-white"
                disabled={!formData.cantonCode}
              >
                <option value="">
                  {formData.cantonCode ? "Seleccione distrito" : "Seleccione cantón primero"}
                </option>
                {distritos.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Otras señas */}
            <div className="sm:col-span-2">
              <label className="block text-sm text-gray-600 mb-1">Otras señas</label>
              <input
                type="text"
                name="otrasSenas"
                placeholder="Casa #, referencia, etc."
                value={formData.otrasSenas}
                onChange={handleChange}
                className="w-full border rounded-lg p-3"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#5A61E5] text-white py-3 rounded-lg font-semibold hover:bg-[#4349c6] transition-colors mt-6"
          >
            {loading ? "Registrando..." : "Registrarme"}
          </button>
        </form>
      </div>
    </div>
  );
}
